// Számla-lista sor-akciók (jog- + státusz-vezérelt). MINDEN soron „Megtekintés" (szem → read-only detail).
// Draft soron: ceruza (szerkesztés, invoices.manage) + kuka (törlés, invoices.delete) + „Kiállítás"
// (invoices.manage). Kiállított/lejárt soron: „Sztornó" (invoices.manage) + díjbekérőnél „Érvénytelenítés".
// Letölthető státuszoknál (issued/paid/overdue/storno): „PDF letöltése". A tényleges izolációt/írás-jogot
// az Odin RLS/scope + a BFF-guard adja; ez a réteg a jog + státusz szerint dönti el, MIT renderel.
import {
  ArrowDownTrayIcon,
  ArrowUturnLeftIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { type TFunction } from 'i18next';

import {
  DeleteRowAction,
  EditRowAction,
  RowActionButton,
  RowActions,
  ViewRowAction,
} from '@/components/shared/list-row-actions';

export interface InvoiceRowActionsRow {
  id: string;
  status: string;
  /** invoice | proforma — a díjbekérő-érvénytelenítés kapuja. */
  type?: string | null;
}

/** A letölthető-PDF státuszok (legacy-parity: draft NEM, érvénytelenített díjbekérő NEM). */
const PDF_STATUSES = new Set(['issued', 'paid', 'overdue', 'storno']);
/** A sztornózható / érvényteleníthető alap-státuszok. */
const ISSUE_ACTIVE = new Set(['issued', 'overdue']);

interface InvoiceRowActionsProps<T extends InvoiceRowActionsRow> {
  row: T;
  canManage: boolean;
  canDelete: boolean;
  onView: (row: T) => void;
  onEdit: (row: T) => void;
  onDelete: (row: T) => void;
  /** Kiállítás (draft) — a Számlázz.hu-életciklus (opcionális; hiányában nincs gomb). */
  onIssue?: (row: T) => void;
  /** Sztornó (issued|overdue). */
  onStorno?: (row: T) => void;
  /** Díjbekérő-érvénytelenítés (proforma + issued|overdue). */
  onCancelProforma?: (row: T) => void;
  /** PDF-letöltés (letölthető státuszok). */
  onPdf?: (row: T) => void;
  /** A PDF-letöltés folyamatban ennél a sornál (a gomb tiltva). */
  pdfPending?: boolean;
  t: TFunction;
}

export function InvoiceRowActions<T extends InvoiceRowActionsRow>({
  row,
  canManage,
  canDelete,
  onView,
  onEdit,
  onDelete,
  onIssue,
  onStorno,
  onCancelProforma,
  onPdf,
  pdfPending = false,
  t,
}: InvoiceRowActionsProps<T>): JSX.Element {
  const isDraft = row.status === 'draft';
  const isActive = ISSUE_ACTIVE.has(row.status);
  const isProforma = row.type === 'proforma';

  const showEdit = isDraft && canManage;
  const showDelete = isDraft && canDelete;
  const showIssue = isDraft && canManage && onIssue !== undefined;
  const showStorno = isActive && canManage && onStorno !== undefined;
  const showCancel = isActive && isProforma && canManage && onCancelProforma !== undefined;
  const showPdf = PDF_STATUSES.has(row.status) && onPdf !== undefined;

  return (
    <RowActions>
      <ViewRowAction label={t('invoices.view')} onClick={() => onView(row)} />
      {showEdit && <EditRowAction label={t('invoices.edit')} onClick={() => onEdit(row)} />}
      {showIssue && (
        <RowActionButton
          label={t('invoices.actions.issue')}
          icon={PaperAirplaneIcon}
          onClick={() => onIssue?.(row)}
        />
      )}
      {showPdf && (
        <RowActionButton
          label={t('invoices.actions.pdf')}
          icon={ArrowDownTrayIcon}
          disabled={pdfPending}
          onClick={() => onPdf?.(row)}
        />
      )}
      {showStorno && (
        <RowActionButton
          label={t('invoices.actions.storno')}
          icon={ArrowUturnLeftIcon}
          onClick={() => onStorno?.(row)}
        />
      )}
      {showCancel && (
        <RowActionButton
          label={t('invoices.actions.cancel')}
          icon={NoSymbolIcon}
          onClick={() => onCancelProforma?.(row)}
        />
      )}
      {showDelete && <DeleteRowAction label={t('invoices.delete')} onClick={() => onDelete(row)} />}
    </RowActions>
  );
}
